import React, { useState } from "react";
import { api } from "~/api";
import "../css/HealthCheck.css";

interface ServiceStatus {
  status: string;
  error?: string;
}

interface HealthStatus {
  s3?: ServiceStatus;
  dynamodb?: ServiceStatus;
  docker?: ServiceStatus;
  overall: string;
}

// ステータスアイコンを取得
const getStatusIcon = (status?: string): string => {
  if (!status) return "⚪";
  return status === "ok" ? "✅" : "❌";
};

// ステータスカラーを取得
const getStatusColor = (status?: string): string => {
  if (!status) return "gray";
  return status === "ok" ? "green" : "red";
};

// ステータステキストを取得
const getStatusText = (status?: string): string => {
  if (!status) return "未確認";
  return status === "ok" ? "正常" : "異常";
};

// サービスステータスアイテムコンポーネント
const ServiceStatusItem: React.FC<{
  name: string;
  status?: ServiceStatus;
}> = ({ name, status }) => (
  <div className="service-item">
    <h3>{name}</h3>
    <div className="status-detail">
      <span className="status-icon">{getStatusIcon(status?.status)}</span>
      <span style={{ color: getStatusColor(status?.status) }}>{getStatusText(status?.status)}</span>
      {status?.error && <p className="error-detail">{status.error}</p>}
    </div>
  </div>
);

const HealthCheckPage: React.FC = () => {
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkHealth = async () => {
    setIsLoading(true);
    setError(null);
    setHealthStatus(null);
    try {
      const result = await api.healthCheck.checkStatus.query();
      setHealthStatus(result);
      // overall が error の場合はエラーメッセージも表示する
      if (result.overall === "error") {
        setError("一部のサービスでエラーが検出されました。下記の詳細を確認してください。");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "ヘルスチェックに失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="health-check-container">
      <h1>死活監視</h1>

      <button className="check-button" onClick={checkHealth} disabled={isLoading}>
        {isLoading ? "確認中..." : "ヘルスチェック実行"}
      </button>

      {error && <div className="error-message">エラー: {error}</div>}

      {healthStatus && (
        <div className="status-container">
          <div className="overall-status">
            <h2>
              総合ステータス: {getStatusIcon(healthStatus.overall)}
              <span style={{ color: getStatusColor(healthStatus.overall) }}>
                {getStatusText(healthStatus.overall)}
              </span>
            </h2>
          </div>

          <div className="services-status">
            <ServiceStatusItem name="S3" status={healthStatus.s3} />
            <ServiceStatusItem name="DynamoDB" status={healthStatus.dynamodb} />
            <ServiceStatusItem name="Docker サービス" status={healthStatus.docker} />
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthCheckPage;
